import React from 'react';
import { useTheme } from '@mui/material';
import { BackendNode } from './types';
import {
    VIEW_W,
    FOCUS_VIEW_H,
    FOCUS_MODEL_X,
    FOCUS_NODE_X,
    FOCUS_Y,
    FOCUS_MODEL_R,
    FOCUS_NODE_R,
    FOCUS_BOTTOM_Y,
    SAT_R,
    LABEL_SLOT_GUTTER,
    getBgColor,
} from './layout';
import {
    CenterNode,
    DisabledTooltip,
    LabelTooltip,
    AlertTriangle,
    isReportMember,
    nodeCircleOpacity,
    nodeLabelOpacity,
    reportOutline,
} from './Nodes';
import { fitLabel } from './fitLabel';
import Legend from './Legend';
import { satelliteColor } from './nodeTypeColors';

const CHILD_LABEL_FONT = 10;
// Figma 697-2588 (child-row labels)
const CHILD_LABEL_COLOR = '#040404';

interface FocusSvgProps {
    centerNode: BackendNode;
    focusedNode: BackendNode;
    relatedNodes: BackendNode[];
    typeColors: Map<string, string>;
    hoveredId: number | null;
    reportLensActive?: boolean;
    reportMemberUids?: ReadonlySet<string>;
    onHover: (id: number | null) => void;
    onClickCenter: (node: BackendNode) => void;
    onClickRelated: (node: BackendNode) => void;
}

export default function FocusSvg({
    centerNode,
    focusedNode,
    relatedNodes,
    typeColors,
    hoveredId,
    reportLensActive = false,
    reportMemberUids,
    onHover,
    onClickCenter,
    onClickRelated,
}: FocusSvgProps) {
    const theme = useTheme();
    const bgColor = getBgColor(theme);
    const spacing = relatedNodes.length > 0 ? VIEW_W / (relatedNodes.length + 1) : VIEW_W / 2;
    const focusedDisabled = focusedNode.enabled_in_concierge_ind === 'N';
    // Each child owns one column of the row; its centred label must stay inside that column,
    // otherwise a dense row (~19 children) smears every name into its neighbours.
    const labelBudget = spacing - LABEL_SLOT_GUTTER;

    return (
        <svg viewBox={`0 0 ${VIEW_W} ${FOCUS_VIEW_H}`} width="100%" overflow="visible" style={{ display: 'block' }}>
            <Legend typeColors={typeColors} reportLensActive={reportLensActive} />

            {/* Edge: model → focused node */}
            <path
                d={`M ${FOCUS_MODEL_X + FOCUS_MODEL_R} ${FOCUS_Y} L ${FOCUS_NODE_X - FOCUS_NODE_R} ${FOCUS_Y}`}
                fill="none"
                stroke="#b8c0cc"
                strokeWidth={1.5}
            />

            {/* Edges: focused node → bottom row */}
            {relatedNodes.map((n, i) => {
                const bx = spacing * (i + 1);
                return (
                    <line
                        key={n.llm_learning_node_id}
                        x1={FOCUS_NODE_X}
                        y1={FOCUS_Y}
                        x2={bx}
                        y2={FOCUS_BOTTOM_Y}
                        stroke="#b8c0cc"
                        strokeWidth={1.5}
                    />
                );
            })}

            {/* Model node (left) */}
            <CenterNode
                node={centerNode}
                cx={FOCUS_MODEL_X}
                cy={FOCUS_Y}
                r={FOCUS_MODEL_R}
                hovered={hoveredId === centerNode.llm_learning_node_id}
                onHover={onHover}
                onClick={onClickCenter}
            />

            {/* Focused node — background mask covers edge terminus */}
            <circle cx={FOCUS_NODE_X} cy={FOCUS_Y} r={FOCUS_NODE_R + 3} fill={bgColor} />
            <CenterNode
                node={focusedNode}
                cx={FOCUS_NODE_X}
                cy={FOCUS_Y}
                r={FOCUS_NODE_R}
                hovered={hoveredId === focusedNode.llm_learning_node_id}
                disabled={focusedDisabled}
                inReport={isReportMember(focusedNode, reportMemberUids)}
                color={satelliteColor(focusedNode.type, focusedNode.node_color)}
                onHover={onHover}
                onClick={() => {}}
            />

            {/* Bottom row: related nodes */}
            {relatedNodes.map((n, i) => {
                const bx = spacing * (i + 1);
                const disabled = n.enabled_in_concierge_ind === 'N';
                const hovered = hoveredId === n.llm_learning_node_id;
                const inReport = isReportMember(n, reportMemberUids);
                const circleOpacity = nodeCircleOpacity(disabled, inReport, hovered);
                const label = fitLabel(n.name, labelBudget, CHILD_LABEL_FONT);
                return (
                    <g
                        key={n.llm_learning_node_id}
                        data-testid="kg-node"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => onHover(n.llm_learning_node_id)}
                        onMouseLeave={() => onHover(null)}
                        onClick={() => onClickRelated(n)}
                    >
                        <circle cx={bx} cy={FOCUS_BOTTOM_Y} r={SAT_R + 3} fill={bgColor} />
                        <circle
                            cx={bx}
                            cy={FOCUS_BOTTOM_Y}
                            r={SAT_R}
                            fill={satelliteColor(n.type, n.node_color)}
                            fillOpacity={circleOpacity}
                            {...reportOutline(inReport)}
                        />
                        <text
                            x={bx}
                            y={FOCUS_BOTTOM_Y + SAT_R + 14}
                            textAnchor="middle"
                            fontSize={CHILD_LABEL_FONT}
                            fill={CHILD_LABEL_COLOR}
                            fillOpacity={nodeLabelOpacity(disabled, inReport, hovered)}
                        >
                            {label.text}
                        </text>
                        {n.alert && (
                            <AlertTriangle
                                x={bx}
                                y={FOCUS_BOTTOM_Y + SAT_R + 32}
                                text={n.alert.text}
                                link={n.alert.link}
                                onHoverNode={onHover}
                            />
                        )}
                        {disabled && hovered && (
                            <DisabledTooltip
                                cx={bx}
                                cy={FOCUS_BOTTOM_Y}
                                name={n.name}
                                reasonText={n.type === 'Date' ? (n.alert?.text ?? undefined) : undefined}
                            />
                        )}
                        {!disabled && hovered && label.truncated && (
                            <LabelTooltip cx={bx} cy={FOCUS_BOTTOM_Y} name={n.name} />
                        )}
                    </g>
                );
            })}
        </svg>
    );
}
